import { type ReactNode, type Ref, useCallback, useState } from 'react'
import cx from 'classnames'
import styles from './SideSheet.module.css'
import { useIsSideSheetAbove } from '@/hooks/viewport-hooks'
import { match } from '@/utils/pattern-match-util'

/**
 * Side sheet elevation variant. `coplanar` is always visible and takes up
 * space, `above` sits above the content.
 */
export type SideSheetElevation = 'coplanar' | 'above'

interface SideSheetHostViewProps {
  elevation?: SideSheetElevation
  children?: ReactNode
  sheetVisible?: boolean
  mainless?: boolean
  onRequestHide?: () => unknown
  ref?: Ref<HTMLDivElement>
}

/**
 * The host view, used to host the primary UI region and
 * a side sheet.
 */
export function SideSheetHostView({
  children,
  onRequestHide,
  sheetVisible,
  mainless,
  elevation = 'coplanar',
  ref,
}: SideSheetHostViewProps) {
  return (
    <div
      ref={ref}
      className={cx(
        styles.root,
        mainless && styles.mainless,
        match(elevation, {
          coplanar: () => styles.coplanar,
          above: () => styles.above,
        }),
        sheetVisible && styles.visible,
      )}
    >
      {children}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- click-to-dismiss backdrop is a pointer affordance, not a control */}
      <div className={styles.overlay} onClick={onRequestHide}></div>
    </div>
  )
}

interface SideSheetMainProps {
  children?: ReactNode
}

/**
 * The main UI region.
 */
export function SideSheetMain({ children }: SideSheetMainProps) {
  return <div className={styles.main}>{children}</div>
}

interface SideSheetProps {
  children?: ReactNode
}

/**
 * The actual side sheet itself.
 */
export function SideSheet({ children }: SideSheetProps) {
  return <div className={styles.sheet}>{children}</div>
}

/**
 * Convenience hook for managing the side sheet state.
 */
export function useSideSheet() {
  const [visible, setVisible] = useState(false)
  const show = useCallback(() => setVisible(true), [])
  const hide = useCallback(() => setVisible(false), [])
  const shouldBeAbove = useIsSideSheetAbove()
  const toggle = useCallback(
    () => (visible ? hide() : show()),
    [visible, hide, show],
  )
  return {
    show,
    hide,
    toggle,
    shouldBeAbove,
    visible,
    elevation: (shouldBeAbove ? 'above' : 'coplanar') as SideSheetElevation,
  }
}
